
import React from 'react';
import { useNavigate } from 'react-router-dom';
import FocusCoach from '@/components/profile/FocusCoach';

interface UserStatsTabProps {
  lists: any[];
  tasks: any[];
  totalPomodoros: number;
}

const UserStatsTab: React.FC<UserStatsTabProps> = ({ lists, tasks, totalPomodoros }) => {
  const navigate = useNavigate();
  
  // Calculate priority breakdowns
  const priorityBreakdown = {
    'do-first': tasks.filter(t => t.priority_quadrant === 'do-first').length,
    'schedule': tasks.filter(t => t.priority_quadrant === 'schedule').length,
    'delegate': tasks.filter(t => t.priority_quadrant === 'delegate').length,
    'eliminate': tasks.filter(t => t.priority_quadrant === 'eliminate').length,
    'unprioritized': tasks.filter(t => !t.priority_quadrant).length,
  };

  const handleCardClick = () => {
    navigate('/tasks');
  };

  return (
    <div className="space-y-4">
      <FocusCoach tasks={tasks} />
      
      <h4 className="text-lg font-medium text-gray-800 dark:text-gray-200">Your workload</h4>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
          <p className="text-sm text-gray-500 dark:text-gray-400">Lists</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{lists.length}</p>
        </div>
        
        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Tasks</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{tasks.length}</p>
        </div>
      </div>

      <div className="space-y-3">
        <h5 className="text-sm font-medium text-gray-600 dark:text-gray-300">Task Priorities</h5>
        
        {/* 2x2 Grid for priority quadrants */}
        <div className="grid grid-cols-2 gap-3">
          <button 
            onClick={handleCardClick}
            className="flex justify-between items-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <span className="text-red-600 dark:text-red-400">🔥</span>
              <span className="text-sm font-medium text-red-800 dark:text-red-200">Do First</span>
            </div>
            <span className="text-lg font-semibold text-red-900 dark:text-red-100">{priorityBreakdown['do-first']}</span>
          </button>
          
          <button 
            onClick={handleCardClick}
            className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <span className="text-blue-600 dark:text-blue-400">🧠</span>
              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">Schedule</span>
            </div>
            <span className="text-lg font-semibold text-blue-900 dark:text-blue-100">{priorityBreakdown['schedule']}</span>
          </button>
          
          <button 
            onClick={handleCardClick}
            className="flex justify-between items-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <span className="text-yellow-600 dark:text-yellow-400">🤝</span>
              <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Delegate</span>
            </div>
            <span className="text-lg font-semibold text-yellow-900 dark:text-yellow-100">{priorityBreakdown['delegate']}</span>
          </button>
          
          <button 
            onClick={handleCardClick}
            className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800/70 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <span className="text-gray-600 dark:text-gray-400">🗑️</span>
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Eliminate</span>
            </div>
            <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{priorityBreakdown['eliminate']}</span>
          </button>
        </div>

        {/* Unprioritized section below the 2x2 grid */}
        <button 
          onClick={handleCardClick}
          className="w-full flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800/70 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-2">
            <span className="text-gray-500 dark:text-gray-400">❓</span>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Unprioritized</span>
          </div>
          <span className="text-lg font-semibold text-gray-700 dark:text-gray-200">{priorityBreakdown['unprioritized']}</span>
        </button>
      </div>
    </div>
  );
};

export default UserStatsTab;
